import React, { Dispatch, SetStateAction } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBan, faEdit, faEye, faSave, faClose } from "@fortawesome/free-solid-svg-icons"
import { MdCategory } from "react-icons/md"
import { Brand, Meeting, Note, Stage } from "../../../../types/brandDatas"
import { numFixer } from "../../../User/getAllUsers/components/Functions"
import { BrandSubSectorsData, defaultStages } from "../../../../static/Brand"
import BrandStateLabel from "./BrandStateLabel"
import {
  BrandColumn,
  BrandListRecord,
  BrandOperationsState,
  BrandStateHistoryItem,
  ColumnConfigResult,
  Quiz,
} from "../types"

interface ColumnConfigParams {
  handleSpamBrand: (brandId: string) => Promise<void> | void
  quizzes: Quiz[]
  handleSalesReadiness: (brandId: string, rate: number) => Promise<void> | void
  handleHeardFrom: (brandId: string, heardFrom: string) => Promise<void> | void
  editRateId: string | null
  setEditRateId: Dispatch<SetStateAction<string | null>>
  rateValues: Record<string, number | undefined>
  setRateValues: Dispatch<SetStateAction<Record<string, number | undefined>>>
  editHeardFromId: string | null
  setEditHeardFromId: Dispatch<SetStateAction<string | null>>
  heardFromValues: Record<string, string>
  setHeardFromValues: Dispatch<SetStateAction<Record<string, string>>>
  onOpenNotesModal: (brandId: string, notes: Note[]) => void
  onOpenMeetingModal: (brandId: string, meetings: Meeting[]) => void
  onOpenOperationsModal: (brandId: string, operations: BrandOperationsState) => void
  onOpenStagesModal: (brandId: string, stages: Stage[]) => void
  onOpenEcommerceModal: (brandId: string, currentValue: string) => void
  onOpenBrandSizeModal: (brandId: string, currentSize: string) => void
  onOpenBrandStateHistoryModal: (
    brandId: string,
    brandName: string,
    brandStateHistory?: BrandStateHistoryItem[],
  ) => void
  onOpenResponsibleAdminModal: (brandId: string, currentAdmins?: string[]) => void
  onOpenCpmAimModal: (brandId: string, currentValue?: number) => void
  onOpenLeadStyleModal: (
    brandId: string,
    currentValue?: "dominance" | "influencing" | "cautious" | "stabilizing",
  ) => void
  onOpenUtmModal: (brandId: string, currentUtm?: Brand["utm"]) => void
  onOpenReferenceModal: (brandId: string, reference?: string) => void
  onOpenClassificationModal: (brandId: string, currentClassification?: any) => void
}

const getSubSectorName = (code: string) => {
  const found = BrandSubSectorsData.find((item) => item.value === code)
  return found ? found.name : code
}

const getStageDisplayName = (stageKey: string) => {
  const stage = defaultStages.find((s) => s.key === stageKey)
  return stage ? stage.display : stageKey
}

export const columnConfig = ({
  handleSpamBrand,
  quizzes,
  handleSalesReadiness,
  handleHeardFrom,
  editRateId,
  setEditRateId,
  rateValues,
  setRateValues,
  editHeardFromId,
  setEditHeardFromId,
  heardFromValues,
  setHeardFromValues,
  onOpenNotesModal,
  onOpenMeetingModal,
  onOpenOperationsModal,
  onOpenStagesModal,
  onOpenEcommerceModal,
  onOpenBrandSizeModal,
  onOpenBrandStateHistoryModal,
  onOpenResponsibleAdminModal,
  onOpenCpmAimModal,
  onOpenLeadStyleModal,
  onOpenUtmModal,
  onOpenReferenceModal,
  onOpenClassificationModal,
}: ColumnConfigParams): ColumnConfigResult => {
  // Kimlik & Temel Bilgiler
  const identityColumns: BrandColumn[] = [
    { accessor: "id", title: "Id" },
    {
      accessor: "brand",
      title: "Detaylar",
      sortable: false,
      render: ({ _id }: BrandListRecord) => (
        <a href={`/brands/find/${_id}`} target="_blank" rel="noopener noreferrer">
          <div className="text-center items-center mr-4">
            <FontAwesomeIcon icon={faEye} style={{ color: "#005eff" }} />
          </div>
        </a>
      ),
    },
    { accessor: "brand_name", title: "Marka İsmi" },
    {
      accessor: "brand_state",
      title: "Marka Durumu",
      sortable: true,
      render: ({ brand_state }: BrandListRecord) => (
        <div>
          <BrandStateLabel brandState={brand_state || ""} showTooltip={true} />
        </div>
      ),
    },
    {
      accessor: "brandStateHistory",
      title: (
        <div className="w-30 whitespace-normal text-center break-words">Marka Durum Detayları</div>
      ),
      render: ({ brandStateHistory, _id, brand_name }: BrandListRecord) => (
        <div className="text-center items-center mr-4">
          <FontAwesomeIcon
            icon={faEye}
            style={{ color: "#005eff", cursor: "pointer" }}
            onClick={() => onOpenBrandStateHistoryModal(_id, brand_name, brandStateHistory)}
          />
        </div>
      ),
    },
    {
      accessor: "brand_size",
      title: "Marka Boyutu",
      render: ({ _id, brand_size = "small" }: BrandListRecord) => {
        const getSizeLabel = (size: string) => {
          switch (size) {
            case "small":
              return "Küçük"
            case "medium":
              return "Orta"
            case "large":
              return "Büyük"
            default:
              return "Küçük"
          }
        }

        return (
          <div className="flex text-center items-center mr-4">
            <div className="flex text-center items-center">
              <p className="text-gray-600 mt-2">{getSizeLabel(brand_size)}</p>
              <FontAwesomeIcon
                icon={faEdit}
                style={{ color: "#005eff", cursor: "pointer", marginLeft: "10px" }}
                onClick={() => onOpenBrandSizeModal(_id, brand_size)}
              />
            </div>
          </div>
        )
      },
    },
    {
      accessor: "sector",
      title: "Sektör",
      render: ({ sector }: BrandListRecord) => <div>{sector === "agency" ? "Ajans" : "Marka"}</div>,
    },
    {
      accessor: "sub_sector",
      title: "Alt Sektör",
      render: ({ sub_sector }: BrandListRecord) => <div>{getSubSectorName(sub_sector || "")}</div>,
    },
    { accessor: "first_name", title: "İsim" },
    { accessor: "last_name", title: "Soyisim" },
    { accessor: "email", title: "Email" },
    { accessor: "phone", title: "Telefon" },
    {
      accessor: "reference",
      title: "Referans",
      render: ({ reference, _id }: BrandListRecord) => (
        <div>
          {reference || "-"}
          <FontAwesomeIcon
            icon={faEdit}
            style={{ color: "#005eff", cursor: "pointer", marginLeft: "10px" }}
            onClick={() => {
              onOpenReferenceModal(_id, reference || "")
            }}
          />
        </div>
      ),
    },
    {
      accessor: "classification",
      title: "Sınıflandırma",
      width: "260px",
      sortable: true,
      render: ({ _id, main_category, sub_categories }: BrandListRecord) => {
        const currentClassification =
          main_category && sub_categories && sub_categories.length > 0
            ? [
                {
                  main_category: {
                    category: main_category,
                    confidence: 1.0,
                  },
                  sub_categories: sub_categories.map((sub: any) => ({
                    category: sub.name || sub.category || sub,
                    confidence: sub.confidence || 1.0,
                  })),
                },
              ]
            : null

        // Extract unique sub category names
        const subCategoryNames =
          sub_categories
            ?.map((sub: any) => sub.name || sub.category || sub)
            .filter((name: string) => name) || []
        const uniques = Array.from(new Set(subCategoryNames))
        const toShow = uniques.slice(0, 3).join(", ")
        const suffix = uniques.length > 3 ? " .." : ""

        return (
          <div
            className="flex items-center gap-2 px-4 py-1.5 border border-gray-300 rounded-2xl cursor-pointer hover:bg-gray-100"
            onClick={() => {
              onOpenClassificationModal(_id, currentClassification)
            }}
          >
            {React.createElement(MdCategory as any, { className: "w-4 h-4 text-black" })}
            <span>{uniques.length > 0 ? toShow + suffix : "Kategori Seç"}</span>
          </div>
        )
      },
    },
    {
      accessor: "created_at",
      title: "Oluşturulma Tarihi",
      sortable: true,
      render: ({ created_at }: BrandListRecord) => (
        <div>{created_at ? new Date(created_at).toLocaleDateString() : "-"}</div>
      ),
    },
    {
      accessor: "last_login",
      title: "Son Giriş",
      sortable: true,
      render: ({ last_login }: BrandListRecord) => (
        <div>
          {last_login
            ? new Date(last_login).toLocaleDateString("tr-TR") +
              " " +
              new Date(last_login).toLocaleTimeString("tr-TR", {
                hour: "2-digit",
                minute: "2-digit",
              })
            : "-"}
        </div>
      ),
    },
  ]

  // Bütçe / Finansal Durum
  const financialColumns: BrandColumn[] = [
    {
      accessor: "balance",
      title: "Bakiye",
      sortable: true,
      render: ({ balance = 0 }: BrandListRecord) => (
        <div>
          <p>{numFixer(balance)}</p>
        </div>
      ),
    },
    {
      accessor: "totalBlocks",
      title: "Bloke Edilen Bakiye",
      sortable: true,
      render: ({ totalBlocks = 0 }: BrandListRecord) => (
        <div>
          <p>{numFixer(totalBlocks)}</p>
        </div>
      ),
    },
    {
      accessor: "totalBalance",
      title: "Toplam Yüklenen Bakiye",
      sortable: true,
      render: ({ totalBalance = 0 }: BrandListRecord) => (
        <div>
          <p>{numFixer(totalBalance)}</p>
        </div>
      ),
    },
    {
      accessor: "totalSpentBalance",
      title: "Toplam Harcanan Bakiye",
      sortable: true,
      render: ({ totalSpentBalance = 0 }: BrandListRecord) => (
        <div>
          <p>{numFixer(totalSpentBalance)}</p>
        </div>
      ),
    },
    {
      accessor: "balanceTotalBudgetRatio",
      title: "Bakiye/Toplam Bütçe Oranı",
      sortable: true,
      render: ({ balanceTotalBudgetRatio }: BrandListRecord) => (
        <div>
          <p>{balanceTotalBudgetRatio ? (balanceTotalBudgetRatio * 100).toFixed(2) + "%" : "-"}</p>
        </div>
      ),
    },
    {
      accessor: "totalSpentToTotalBalanceRatio",
      title: "Harcanan/Toplam Bakiye Oranı",
      sortable: true,
      render: ({ totalSpentToTotalBalanceRatio }: BrandListRecord) => (
        <div>
          <p>
            {totalSpentToTotalBalanceRatio
              ? (totalSpentToTotalBalanceRatio * 100).toFixed(2) + "%"
              : "-"}
          </p>
        </div>
      ),
    },
    {
      accessor: "totalBlockedToTotalBalanceRatio",
      title: "Bloke/Toplam Bakiye Oranı",
      sortable: true,
      render: ({ totalBlockedToTotalBalanceRatio }: BrandListRecord) => (
        <div>
          <p>
            {totalBlockedToTotalBalanceRatio
              ? (totalBlockedToTotalBalanceRatio * 100).toFixed(2) + "%"
              : "-"}
          </p>
        </div>
      ),
    },
  ]

  // Kampanya Verileri
  const campaignColumns: BrandColumn[] = [
    {
      accessor: "totalCampaigns",
      title: "Toplam Kampanyalar",
      sortable: true,
      render: ({ totalCampaigns = 0 }: BrandListRecord) => (
        <div>
          <p>{numFixer(totalCampaigns)}</p>
        </div>
      ),
    },
    {
      accessor: "activeCampaigns",
      title: "Aktif Kampanyalar",
      sortable: true,
      render: ({ activeCampaigns = 0 }: BrandListRecord) => (
        <div>
          <p>{numFixer(activeCampaigns)}</p>
        </div>
      ),
    },
    {
      accessor: "inactiveCampaigns",
      title: "Pasif Kampanyalar",
      sortable: true,
      render: ({ inactiveCampaigns = 0 }: BrandListRecord) => (
        <div>
          <p>{numFixer(inactiveCampaigns)}</p>
        </div>
      ),
    },
    {
      accessor: "draftCampaigns",
      title: "Taslak Kampanyalar",
      sortable: true,
      render: ({ draftCampaigns = 0 }: BrandListRecord) => (
        <div>
          <p>{numFixer(draftCampaigns)}</p>
        </div>
      ),
    },
    {
      accessor: "activeCampaignsToTotalCampaignsRatio",
      title: "Aktif/Toplam Kampanyalar Oranı",
      sortable: true,
      render: ({ activeCampaignsToTotalCampaignsRatio }: BrandListRecord) => (
        <div>
          <p>
            {activeCampaignsToTotalCampaignsRatio
              ? (activeCampaignsToTotalCampaignsRatio * 100).toFixed(2) + "%"
              : "-"}
          </p>
        </div>
      ),
    },
    {
      accessor: "inactiveCampaignsToTotalCampaignsRatio",
      title: "Pasif/Toplam Kampanyalar Oranı",
      sortable: true,
      render: ({ inactiveCampaignsToTotalCampaignsRatio }: BrandListRecord) => (
        <div>
          <p>
            {inactiveCampaignsToTotalCampaignsRatio
              ? (inactiveCampaignsToTotalCampaignsRatio * 100).toFixed(2) + "%"
              : "-"}
          </p>
        </div>
      ),
    },
    {
      accessor: "draftCampaignsToTotalCampaignsRatio",
      title: "Taslak/Toplam Kampanyalar Oranı",
      sortable: true,
      render: ({ draftCampaignsToTotalCampaignsRatio }: BrandListRecord) => (
        <div>
          <p>
            {draftCampaignsToTotalCampaignsRatio
              ? (draftCampaignsToTotalCampaignsRatio * 100).toFixed(2) + "%"
              : "-"}
          </p>
        </div>
      ),
    },
  ]

  // Başvuru Verileri
  const applicationColumns: BrandColumn[] = [
    {
      accessor: "totalApplications",
      title: "Toplam Başvurular",
      sortable: true,
      render: ({ totalApplications = 0 }: BrandListRecord) => (
        <div>
          <p>{numFixer(totalApplications)}</p>
        </div>
      ),
    },
    {
      accessor: "activeApplications",
      title: "Aktif Başvurular",
      sortable: true,
      render: ({ activeApplications = 0 }: BrandListRecord) => (
        <div>
          <p>{numFixer(activeApplications)}</p>
        </div>
      ),
    },
    {
      accessor: "inactiveApplications",
      title: "Pasif Başvurular",
      sortable: true,
      render: ({ inactiveApplications = 0 }: BrandListRecord) => (
        <div>
          <p>{numFixer(inactiveApplications)}</p>
        </div>
      ),
    },
    {
      accessor: "activeApplicationsToTotalApplicationsRatio",
      title: "Aktif/Toplam Başvurular Oranı",
      sortable: true,
      render: ({ activeApplicationsToTotalApplicationsRatio }: BrandListRecord) => (
        <div>
          <p>
            {activeApplicationsToTotalApplicationsRatio
              ? (activeApplicationsToTotalApplicationsRatio * 100).toFixed(2) + "%"
              : "-"}
          </p>
        </div>
      ),
    },
    {
      accessor: "inactiveApplicationsToTotalApplicationsRatio",
      title: "Pasif/Toplam Başvurular Oranı",
      sortable: true,
      render: ({ inactiveApplicationsToTotalApplicationsRatio }: BrandListRecord) => (
        <div>
          <p>
            {inactiveApplicationsToTotalApplicationsRatio
              ? (inactiveApplicationsToTotalApplicationsRatio * 100).toFixed(2) + "%"
              : "-"}
          </p>
        </div>
      ),
    },
    {
      accessor: "costPerApplication",
      title: "Başvuru Başına Harcanan Bakiye",
      sortable: true,
      render: ({ costPerApplication = 0 }: BrandListRecord) => (
        <div>
          <p>{numFixer(costPerApplication)}</p>
        </div>
      ),
    },
    {
      accessor: "costPerView",
      title: "Görüntülenme Başına Harcanan Bakiye",
      sortable: true,
      render: ({ costPerView = 0 }: BrandListRecord) => (
        <div>
          <p>{numFixer(costPerView)}</p>
        </div>
      ),
    },
    {
      accessor: "costPerClick",
      title: "Tıklanma Başına Harcanan Bakiye",
      sortable: true,
      render: ({ costPerClick = 0 }: BrandListRecord) => (
        <div>
          <p>{numFixer(costPerClick)}</p>
        </div>
      ),
    },
  ]

  // Ek Notlar ve İletişim Geçmişi
  const notesColumns: BrandColumn[] = [
    {
      accessor: "is_spam",
      title: "Spam",
      render: ({ _id }: BrandListRecord) => (
        <div>
          <FontAwesomeIcon
            icon={faBan}
            style={{ color: "green", cursor: "pointer", marginLeft: "10px" }}
            onClick={() => handleSpamBrand(_id)}
          />
        </div>
      ),
    },
    {
      accessor: "notes",
      title: "Notlar",
      render: ({ _id, notes: initialNotes = [] }: BrandListRecord) => (
        <div className="text-center items-center mr-4">
          <div className="text-sm">
            {initialNotes && initialNotes.length > 0 ? (
              <div>
                <p className="font-medium">{initialNotes.length} not</p>
                <p className="text-gray-500 text-xs">
                  Son not:{" "}
                  {new Date(initialNotes[initialNotes.length - 1].note_time).toLocaleDateString(
                    "tr-TR",
                  )}
                </p>
              </div>
            ) : (
              <span className="text-gray-400">Not yok</span>
            )}
          </div>
          <FontAwesomeIcon
            icon={faEdit}
            style={{ color: "#005eff", cursor: "pointer", marginLeft: "10px" }}
            onClick={() => onOpenNotesModal(_id, initialNotes)}
          />
        </div>
      ),
    },
    {
      accessor: "meetings",
      title: "Toplantılar",
      render: ({ _id, meetings: initialMeetings = [] }: BrandListRecord) => (
        <div className="text-center items-center mr-4">
          <div className="text-sm">
            {initialMeetings && initialMeetings.length > 0 ? (
              <div>
                <p className="font-medium">{initialMeetings.length} toplantı</p>
                <p className="text-gray-500 text-xs">
                  Son toplantı:{" "}
                  {new Date(initialMeetings[initialMeetings.length - 1].time).toLocaleDateString(
                    "tr-TR",
                  )}
                </p>
              </div>
            ) : (
              <span className="text-gray-400">Toplantı yok</span>
            )}
          </div>
          <FontAwesomeIcon
            icon={faEdit}
            style={{ color: "#005eff", cursor: "pointer", marginLeft: "10px" }}
            onClick={() => onOpenMeetingModal(_id, initialMeetings)}
          />
        </div>
      ),
    },
    {
      accessor: "responsible_admin",
      title: "Sorumlu Admin",
      render: ({ _id, responsible_admin }: BrandListRecord) => {
        // Helper function to extract emails from admin data
        const getAdminEmails = (adminData: Brand["responsible_admin"]): string[] => {
          if (!adminData) {
            return []
          }

          if (Array.isArray(adminData)) {
            return adminData
              .map((admin) => {
                if (typeof admin === "string") {
                  return admin
                }
                if (admin && typeof admin === "object" && admin.email) {
                  return admin.email
                }
                return ""
              })
              .filter((email) => email !== "")
          }

          if (typeof adminData === "string") {
            return [adminData]
          }

          return []
        }

        const adminEmails = getAdminEmails(responsible_admin)

        return (
          <div className="text-center items-center mr-4">
            <div className="text-sm">
              {adminEmails.length > 0 ? (
                <div>
                  {adminEmails.map((email, index) => (
                    <p key={index} className="font-medium text-xs">
                      {email}
                    </p>
                  ))}
                </div>
              ) : (
                <span className="text-gray-400">Sorumlu Admin Yok</span>
              )}
            </div>
            <FontAwesomeIcon
              icon={faEdit}
              style={{ color: "#005eff", cursor: "pointer", marginLeft: "10px" }}
              onClick={() => onOpenResponsibleAdminModal(_id, adminEmails)}
            />
          </div>
        )
      },
    },
    {
      accessor: "sales_readiness",
      title: "Satışa Sıcaklık",
      sortable: true,
      render: ({ _id, customer_success_input }: BrandListRecord) => (
        <div className="flex text-center items-center mr-4">
          {editRateId === _id ? (
            <div className="text-center items-center mr-4">
              <input
                type="number"
                value={rateValues[_id] ?? customer_success_input?.sales_readiness ?? ""}
                className="form-input text-sm border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 pt-1 pb-1"
                onChange={(e) =>
                  setRateValues((prev) => ({
                    ...prev,
                    [_id]: e.target.value === "" ? undefined : Number(e.target.value),
                  }))
                }
              />
              <FontAwesomeIcon
                icon={faSave}
                style={{ color: "#005eff", cursor: "pointer", marginLeft: "10px" }}
                onClick={() => {
                  const pendingRate = rateValues[_id] ?? customer_success_input?.sales_readiness
                  if (typeof pendingRate === "number") {
                    handleSalesReadiness(_id, pendingRate)
                  }
                  setEditRateId(null)
                }}
              />
              <FontAwesomeIcon
                icon={faClose}
                style={{ color: "#005eff", cursor: "pointer", marginLeft: "12px" }}
                onClick={() => setEditRateId(null)}
              />
            </div>
          ) : (
            <div className="flex text-center items-center">
              <p className="text-gray-600 mt-2">{customer_success_input?.sales_readiness ?? "-"}</p>
              <FontAwesomeIcon
                icon={faEdit}
                style={{ color: "#005eff", cursor: "pointer", marginLeft: "10px" }}
                onClick={() => {
                  setEditRateId(_id)
                  setRateValues((prev) => ({
                    ...prev,
                    [_id]: customer_success_input?.sales_readiness,
                  }))
                }}
              />
            </div>
          )}
        </div>
      ),
    },
  ]

  // Pazarlama & Strateji Bilgileri
  const marketingColumns: BrandColumn[] = [
    {
      accessor: "cpm_aim",
      title: "CPM Aim",
      sortable: true,
      render: ({ _id, cpm_aim }: BrandListRecord) => (
        <div className="flex text-center items-center mr-4">
          <div className="flex text-center items-center">
            <p className="text-gray-600 mt-2">{cpm_aim || "Belirtilmemiş"}</p>
            <FontAwesomeIcon
              icon={faEdit}
              style={{ color: "#005eff", cursor: "pointer", marginLeft: "10px" }}
              onClick={() => onOpenCpmAimModal(_id, cpm_aim)}
            />
          </div>
        </div>
      ),
    },
    {
      accessor: "lead_style",
      title: "Lead Style",
      sortable: true,
      render: ({ _id, lead_style }: BrandListRecord) => {
        const getLeadStyleLabel = (value: string) => {
          switch (value) {
            case "dominance":
              return "Dominant"
            case "influencing":
              return "Etkileyici"
            case "cautious":
              return "Dikkatli"
            case "stabilizing":
              return "Stabilize"
            default:
              return value
          }
        }

        return (
          <div className="flex text-center items-center mr-4">
            <div className="flex text-center items-center">
              <p className="text-gray-600 mt-2">
                {lead_style ? getLeadStyleLabel(lead_style) : "Belirtilmemiş"}
              </p>
              <FontAwesomeIcon
                icon={faEdit}
                style={{ color: "#005eff", cursor: "pointer", marginLeft: "10px" }}
                onClick={() => onOpenLeadStyleModal(_id, lead_style)}
              />
            </div>
          </div>
        )
      },
    },
    {
      accessor: "e_commerce_infrastructure",
      title: "E-Ticaret Altyapısı",
      sortable: true,
      render: ({ _id, customer_success_input }: BrandListRecord) => (
        <div className="flex text-center items-center mr-4">
          <div className="flex text-center items-center">
            <p className="text-gray-600 mt-2">
              {customer_success_input?.e_commerce_infrastructure || "Belirtilmemiş"}
            </p>
            <FontAwesomeIcon
              icon={faEdit}
              style={{ color: "#005eff", cursor: "pointer", marginLeft: "10px" }}
              onClick={() =>
                onOpenEcommerceModal(_id, customer_success_input?.e_commerce_infrastructure || "")
              }
            />
          </div>
        </div>
      ),
    },
    {
      accessor: "utm",
      title: "UTM Parametreleri",
      sortable: false,
      render: ({ _id, utm }: BrandListRecord) => (
        <div className="flex text-center items-center mr-4">
          <div className="flex text-center items-center">
            <div className="text-gray-600 mt-2 text-xs">
              <div>Source: {utm?.utm_source || "Belirtilmemiş"}</div>
              <div>Medium: {utm?.utm_medium || "Belirtilmemiş"}</div>
              <div>Campaign: {utm?.utm_campaign || "Belirtilmemiş"}</div>
            </div>
            <FontAwesomeIcon
              icon={faEdit}
              style={{ color: "#005eff", cursor: "pointer", marginLeft: "10px" }}
              onClick={() => onOpenUtmModal(_id, utm)}
            />
          </div>
        </div>
      ),
    },
    {
      accessor: "heardFrom",
      title: "Nereden Duyuldu",
      sortable: true,
      render: ({ _id, heardFrom }: BrandListRecord) => (
        <div className="flex text-center items-center mr-4">
          {editHeardFromId === _id ? (
            <div className="text-center items-center mr-4">
              <input
                type="text"
                value={heardFromValues[_id] ?? heardFrom ?? ""}
                className="form-input text-sm border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 pt-1 pb-1"
                onChange={(e) => setHeardFromValues({ ...heardFromValues, [_id]: e.target.value })}
              />
              <FontAwesomeIcon
                icon={faSave}
                style={{ color: "#005eff", cursor: "pointer", marginLeft: "10px" }}
                onClick={() => {
                  const updatedHeardFrom = heardFromValues[_id] ?? heardFrom ?? ""
                  handleHeardFrom(_id, updatedHeardFrom)
                  setEditHeardFromId(null)
                }}
              />
              <FontAwesomeIcon
                icon={faClose}
                style={{ color: "#005eff", cursor: "pointer", marginLeft: "12px" }}
                onClick={() => setEditHeardFromId(null)}
              />
            </div>
          ) : (
            <div className="flex text-center items-center">
              <p className="text-gray-600 mt-2">{heardFrom || "-"}</p>
              <FontAwesomeIcon
                icon={faEdit}
                style={{ color: "#005eff", cursor: "pointer", marginLeft: "10px" }}
                onClick={() => {
                  setEditHeardFromId(_id)
                  setHeardFromValues((prev) => ({
                    ...prev,
                    [_id]: heardFrom ?? "",
                  }))
                }}
              />
            </div>
          )}
        </div>
      ),
    },
    {
      accessor: "stages",
      title: "Aşamalar",
      render: ({ _id, stages: initialStages = [] }: BrandListRecord) => (
        <div className="text-center items-center mr-4">
          {initialStages && initialStages.length > 0 ? (
            <div className="flex items-center">
              <div>
                <p className="text-black-600 font-bold">
                  {getStageDisplayName(initialStages[0].stage)}
                </p>
                <p className="text-gray-600 text-sm">
                  {new Date(initialStages[0].stage_time).toLocaleString("tr-TR")}
                </p>
              </div>
              <FontAwesomeIcon
                icon={faEdit}
                style={{
                  color: "#005eff",
                  cursor: "pointer",
                  marginLeft: "10px",
                }}
                onClick={() => onOpenStagesModal(_id, initialStages)}
              />
            </div>
          ) : (
            <div className="flex items-center">
              <span className="text-gray-400">Aşama seçilmemiş</span>
              <FontAwesomeIcon
                icon={faEdit}
                style={{
                  color: "#005eff",
                  cursor: "pointer",
                  marginLeft: "10px",
                }}
                onClick={() => onOpenStagesModal(_id, [])}
              />
            </div>
          )}
        </div>
      ),
    },
    {
      accessor: "actions",
      title: "Marka İşlemleri",
      render: ({
        _id,
        meeting_planned = false,
        information_mail_sent = false,
        directed_to_agency_based_work = false,
        demo_campaign_created = false,
        balance_loaded = false,
        campaign_management_continues = false,
        campaign_completed = false,
        personal_account = false,
      }: BrandListRecord) => {
        const activeOperations: string[] = []

        if (meeting_planned) {
          activeOperations.push("Toplantı Planlandı")
        }
        if (information_mail_sent) {
          activeOperations.push("Bilgi Maili Gönderildi")
        }
        if (directed_to_agency_based_work) {
          activeOperations.push("Ajans İşine Yönlendirildi")
        }
        if (demo_campaign_created) {
          activeOperations.push("Demo Kampanya Oluşturuldu")
        }
        if (balance_loaded) {
          activeOperations.push("Bakiye Yüklendi")
        }
        if (campaign_management_continues) {
          activeOperations.push("Kampanya Yönetimi Devam Ediyor")
        }
        if (campaign_completed) {
          activeOperations.push("Kampanya Tamamlandı")
        }
        if (personal_account) {
          activeOperations.push("Kişisel Hesap")
        }

        return (
          <div className="text-center items-center mr-4">
            <div className="text-sm mb-2">
              {activeOperations.length > 0 ? (
                <div>
                  {activeOperations.slice(0, 3).map((operation, index) => (
                    <p key={index} className="text-gray-500 text-xs font-medium">
                      {operation}
                    </p>
                  ))}
                  {activeOperations.length > 3 && (
                    <p className="text-gray-500 text-xs">
                      +{activeOperations.length - 3} işlem daha
                    </p>
                  )}
                </div>
              ) : (
                <span className="text-gray-400 text-xs">Aktif işlem yok</span>
              )}
            </div>
            <FontAwesomeIcon
              icon={faEdit}
              style={{ color: "#005eff", cursor: "pointer" }}
              onClick={() =>
                onOpenOperationsModal(_id, {
                  meeting_planned,
                  information_mail_sent,
                  directed_to_agency_based_work,
                  demo_campaign_created,
                  balance_loaded,
                  campaign_management_continues,
                  campaign_completed,
                  personal_account,
                })
              }
            />
          </div>
        )
      },
    },
  ]

  // Quiz columns
  const quizColumns: BrandColumn[] = quizzes.map((quiz) => {
    return {
      accessor: `quiz_${quiz._id}`,
      title: <div className="w-40 whitespace-normal text-center break-words">{quiz.question}</div>,
      sortable: false,
      render: ({ quiz_answers = [] }: BrandListRecord) => {
        const found = quiz_answers.find((quizAns) => quizAns?.key?.question === quiz.key)
        const answer = found ? found.options[0].optionText : null
        return (
          <div className="text-center items-center mr-4">
            {answer ? <span>{answer}</span> : <span className="text-gray-500">Cevaplanmadı</span>}
          </div>
        )
      },
    }
  })

  return {
    identityColumns,
    financialColumns,
    campaignColumns,
    applicationColumns,
    notesColumns,
    marketingColumns,
    quizColumns,
  }
}
